import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

export type OnFarmImplantMatingAllocation = {
  plate: number | null;
  priority: number | null;
  ivcNumber: number | null;
  cleaved: number | null;
  labShipped: number | null;
  heldTransfer: number | null;
  shippedClient: number | null;
  togFrozen: number | null;
  shippedTog: number | null;
  heldFrozen: number | null;
  clientTransfer: number | null;
  clientFrozen: number | null;
  clientDiscarded: number | null;
  discardedGrade1: number | null;
  discardedGrade2: number | null;
  discardedGrade3: number | null;
  degenerates: number | null;
  loadedInStraws: number | null;
  billableShipped: number | null;
  comments: string | null;
};

export type OnFarmMatingRecord = {
  id: string;
  recordName: string | null;
  donorName?: string | null;
  sireName?: string | null;
  accountId: string | null;
  accountName: string | null;
  ranchId?: string | null;
  status: string | null;
  date: string | null;
  embryoCount: number;
  allocation?: OnFarmImplantMatingAllocation | null;
};

export type OnFarmImplantStageGradeCount = {
  stage: number;
  grade: number;
  count: number;
};

export type CreateOnFarmImplantFreshEmbryosRequest = {
  matingId: string;
  accountId?: string | null;
  startingStrawNumber: number;
  entries: OnFarmImplantStageGradeCount[];
};

export type OnFarmImplantFreshEmbryo = {
  sessionEmbryoId: string;
  embryoId: string;
  matingId: string;
  matingName: string | null;
  donorName: string | null;
  sireName: string | null;
  accountId: string | null;
  accountName: string | null;
  strawNumber: number;
  stage: number;
  grade: number;
  queueState: 'Loaded' | 'Implanted' | 'Discarded';
};

export type EditOnFarmImplantFreshEmbryosRequest = {
  sessionEmbryoIds: string[];
  startingStrawNumber?: number | null;
  stage?: number | null;
  grade?: number | null;
  preview?: boolean;
};

export type OnFarmImplantFreshEmbryoEdit = {
  sessionEmbryoId: string;
  embryoId: string;
  queueState: 'Loaded' | 'Implanted';
  oldStrawNumber: number | null;
  newStrawNumber: number | null;
  oldStage: number | null;
  newStage: number | null;
  oldGrade: number | null;
  newGrade: number | null;
};

export type OnFarmImplantFreshEmbryoEditResponse = {
  applied: boolean;
  changes: OnFarmImplantFreshEmbryoEdit[];
  conflictingStrawNumbers: number[];
};

export type OnFarmSourceSummary = {
  sourceType: string;
  items: OnFarmMatingRecord[];
  hasMore: boolean;
};

export type OnFarmSourceTotals = {
  totalRecords: number;
  totalEmbryos: number;
};

export type OnFarmTotalsResponse = {
  fresh: OnFarmSourceTotals;
  frozen: OnFarmSourceTotals;
  flush: OnFarmSourceTotals;
  clientOnFarm: OnFarmSourceTotals;
};

export type MatingsSourceType = 'fresh' | 'frozen' | 'flush' | 'client-on-farm';

export type MatingsQueryParams = {
  search?: string;
  skip?: number;
  take?: number;
  order?: string;
};

export type OnFarmImplantSessionStatus = 'Draft' | 'Active' | 'Completed';

export type OnFarmImplantSession = {
  sessionId: string;
  sessionDate: string;
  siteId: string;
  siteName: string | null;
  accountId: string | null;
  accountName: string | null;
  tripId: string | null;
  tripNumber: string | null;
  status: OnFarmImplantSessionStatus;
  createdById: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  sessionNotes: string | null;
};

export type OnFarmImplantSessionsResponse = {
  items: OnFarmImplantSession[];
  hasMore: boolean;
};

export type OnFarmImplantSessionFilters = {
  siteId?: string;
  accountId?: string;
  tripId?: string;
  createdById?: string;
  sessionDate?: string;
  status?: OnFarmImplantSessionStatus;
  search?: string;
  skip?: number;
  take?: number;
};

export type CreateOnFarmImplantSessionInput = {
  siteId: string;
  sessionDate: string;
  accountId?: string | null;
  tripId?: string | null;
  sessionNotes?: string | null;
};

export type UpdateOnFarmImplantSessionInput = {
  siteId?: string;
  sessionDate?: string;
  accountId?: string;
  clearAccount?: boolean;
  tripId?: string;
  clearTrip?: boolean;
  status?: OnFarmImplantSessionStatus;
  sessionNotes?: string;
};

export type OnFarmImplantTrip = {
  tripId: string;
  tripNumber: string;
  startUtc: string | null;
};

export type OnFarmImplantLookupOption = {
  id: string;
  name: string;
};

export type OnFarmImplantSessionFilterOptions = {
  trips: OnFarmImplantLookupOption[];
  accounts: OnFarmImplantLookupOption[];
  createdBy: OnFarmImplantLookupOption[];
};

function getApiBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getAuthHeaders(): Record<string, string> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export class OnFarmImplantRequestError extends Error {
  readonly status: number;
  readonly problems: string[];

  constructor(status: number, problems: string[]) {
    super(`HTTP error! status: ${status}`);
    this.name = 'OnFarmImplantRequestError';
    this.status = status;
    this.problems = problems;
  }
}

async function readProblems(response: Response): Promise<string[]> {
  try {
    const payload = (await response.json()) as {
      problems?: string[];
      message?: string;
    };
    if (payload?.problems?.length) {
      return payload.problems;
    }
    return payload?.message ? [payload.message] : [];
  } catch {
    return [];
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: getAuthHeaders(),
    ...init,
  });

  if (!response.ok) {
    throw new OnFarmImplantRequestError(
      response.status,
      await readProblems(response),
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

function matingsPath(
  sourceType: MatingsSourceType,
  searchParams: URLSearchParams,
): string {
  return `/api/on-farm-implant/matings/${sourceType}?${searchParams.toString()}`;
}

function buildMatingsParams({
  siteId,
  accountId,
  queryParams,
}: {
  siteId: string;
  accountId?: string;
  queryParams?: MatingsQueryParams;
}): URLSearchParams {
  const searchParams = new URLSearchParams();
  searchParams.set('siteId', siteId);
  if (accountId) {
    searchParams.set('accountId', accountId);
  }
  if (queryParams?.search) {
    searchParams.set('search', queryParams.search);
  }
  if (queryParams?.skip != null) {
    searchParams.set('skip', String(queryParams.skip));
  }
  if (queryParams?.take != null) {
    searchParams.set('take', String(queryParams.take));
  }
  if (queryParams?.order) {
    searchParams.set('order', queryParams.order);
  }
  return searchParams;
}

export const onFarmMatingsOptions = ({
  sourceType,
  siteId,
  accountId,
  queryParams,
}: {
  sourceType: MatingsSourceType;
  siteId: string;
  accountId?: string;
  queryParams?: MatingsQueryParams;
}) =>
  queryOptions({
    queryKey: [
      'on-farm-implant',
      'matings',
      sourceType,
      { siteId, accountId, ...queryParams },
    ],
    async queryFn() {
      return request<OnFarmSourceSummary>(
        matingsPath(
          sourceType,
          buildMatingsParams({ siteId, accountId, queryParams }),
        ),
      );
    },
  });

export function createOnFarmMatingsOptions(
  sourceType: MatingsSourceType,
  siteId: string,
  accountId: string | undefined,
  searchParams: URLSearchParams,
) {
  return queryOptions({
    queryKey: [
      'on-farm-implant',
      'matings',
      sourceType,
      siteId,
      accountId,
      searchParams.toString(),
    ],
    async queryFn() {
      const base = new URLSearchParams(searchParams);
      base.set('siteId', siteId);
      if (accountId) {
        base.set('accountId', accountId);
      }

      return request<OnFarmSourceSummary>(matingsPath(sourceType, base));
    },
  });
}

export const onFarmTotalsOptions = ({
  siteId,
  accountId,
}: {
  siteId: string;
  accountId?: string;
}) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'matings', 'totals', { siteId, accountId }],
    async queryFn() {
      const searchParams = new URLSearchParams();
      searchParams.set('siteId', siteId);
      if (accountId) {
        searchParams.set('accountId', accountId);
      }

      return request<OnFarmTotalsResponse>(
        `/api/on-farm-implant/matings/totals?${searchParams.toString()}`,
      );
    },
  });

export function buildSessionsParams(
  filters: OnFarmImplantSessionFilters,
): URLSearchParams {
  const searchParams = new URLSearchParams();
  const { search, skip, take, ...rest } = filters;

  Object.entries(rest).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, String(value));
    }
  });

  if (search) {
    searchParams.set('search', search);
  }
  if (skip != null) {
    searchParams.set('skip', String(skip));
  }
  if (take != null) {
    searchParams.set('take', String(take));
  }

  return searchParams;
}

export const onFarmImplantSessionsOptions = (
  filters: OnFarmImplantSessionFilters = {},
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'sessions', filters],
    async queryFn() {
      return request<OnFarmImplantSessionsResponse>(
        `/api/on-farm-implant/sessions?${buildSessionsParams(filters).toString()}`,
      );
    },
  });

export const onFarmImplantSessionOptions = (sessionId: string) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'session', sessionId],
    async queryFn() {
      return request<OnFarmImplantSession>(
        `/api/on-farm-implant/sessions/${sessionId}`,
      );
    },
  });

export const onFarmImplantSessionFilterOptions = (siteId?: string) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'session-filter-options', siteId],
    async queryFn() {
      const searchParams = new URLSearchParams();
      if (siteId) {
        searchParams.set('siteId', siteId);
      }

      return request<OnFarmImplantSessionFilterOptions>(
        `/api/on-farm-implant/sessions/filter-options?${searchParams.toString()}`,
      );
    },
  });

export const onFarmImplantTripsOptions = ({
  search,
  take,
}: {
  search?: string;
  take?: number;
} = {}) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'trips', { search, take }],
    async queryFn() {
      const searchParams = new URLSearchParams();
      if (search) {
        searchParams.set('search', search);
      }
      if (take != null) {
        searchParams.set('take', String(take));
      }

      return request<OnFarmImplantTrip[]>(
        `/api/on-farm-implant/trips?${searchParams.toString()}`,
      );
    },
  });

export function createOnFarmImplantSession(
  input: CreateOnFarmImplantSessionInput,
) {
  return request<OnFarmImplantSession>('/api/on-farm-implant/sessions', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateOnFarmImplantSession(
  sessionId: string,
  input: UpdateOnFarmImplantSessionInput,
) {
  return request<OnFarmImplantSession>(
    `/api/on-farm-implant/sessions/${sessionId}`,
    {
      method: 'PUT',
      body: JSON.stringify(input),
    },
  );
}

export const useCreateOnFarmImplantSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateOnFarmImplantSessionInput) =>
      createOnFarmImplantSession(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'sessions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session-filter-options'],
      });
    },
  });
};

export const useUpdateOnFarmImplantSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: UpdateOnFarmImplantSessionInput;
    }) => updateOnFarmImplantSession(sessionId, input),
    onSuccess: (session) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'sessions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', session.sessionId],
      });
    },
  });
};

export type OnFarmImplantApiSourceType = MatingsSourceType | 'non-tog';

export type OnFarmImplantUiSourceType =
  | 'fresh'
  | 'frozen'
  | 'flush'
  | 'client-on-farm-tog'
  | 'non-tog';

export type OnFarmImplantRecordKey = {
  recordId: string;
  accountId: string | null;
};

export type OnFarmImplantSessionSource = {
  sourceTypeId: string;
  sourceType: OnFarmImplantApiSourceType;
  sequenceOrder: number;
  records: OnFarmImplantRecordKey[];
};

export type OnFarmImplantSelection = {
  sources: OnFarmImplantSessionSource[];
};

export type OnFarmImplantSourceSelectionRequest = {
  sourceType: OnFarmImplantApiSourceType;
  sequenceOrder: number;
  records: OnFarmImplantRecordKey[];
};

export type SaveSelectionRequest = {
  sources: OnFarmImplantSourceSelectionRequest[];
};

export type OnFarmImplantRecordIds = {
  sourceType: string;
  records: OnFarmImplantRecordKey[];
  totalRecords: number;
};

const RECORD_KEY_SEPARATOR = '|';

export function toRecordKey(
  recordId: string,
  accountId?: string | null,
): string {
  return `${recordId}${RECORD_KEY_SEPARATOR}${accountId ?? ''}`;
}

export function fromRecordKey(key: string): OnFarmImplantRecordKey {
  const separatorIndex = key.indexOf(RECORD_KEY_SEPARATOR);
  if (separatorIndex === -1) {
    return { recordId: key, accountId: null };
  }
  const accountId = key.slice(separatorIndex + 1);
  return {
    recordId: key.slice(0, separatorIndex),
    accountId: accountId || null,
  };
}

export const ON_FARM_IMPLANT_SOURCE_SEQUENCE: OnFarmImplantUiSourceType[] = [
  'fresh',
  'frozen',
  'flush',
  'client-on-farm-tog',
  'non-tog',
];

const UI_TO_API_SOURCE_TYPE: Record<
  OnFarmImplantUiSourceType,
  OnFarmImplantApiSourceType
> = {
  fresh: 'fresh',
  frozen: 'frozen',
  flush: 'flush',
  'client-on-farm-tog': 'client-on-farm',
  'non-tog': 'non-tog',
};

const API_TO_UI_SOURCE_TYPE: Record<
  OnFarmImplantApiSourceType,
  OnFarmImplantUiSourceType
> = {
  fresh: 'fresh',
  frozen: 'frozen',
  flush: 'flush',
  'client-on-farm': 'client-on-farm-tog',
  'non-tog': 'non-tog',
};

export function toApiSourceType(
  sourceType: string,
): OnFarmImplantApiSourceType {
  return (
    UI_TO_API_SOURCE_TYPE[sourceType as OnFarmImplantUiSourceType] ??
    (sourceType as OnFarmImplantApiSourceType)
  );
}

export function toUiSourceType(sourceType: string): OnFarmImplantUiSourceType {
  return (
    API_TO_UI_SOURCE_TYPE[sourceType as OnFarmImplantApiSourceType] ??
    (sourceType as OnFarmImplantUiSourceType)
  );
}

async function selectionRequest<T>(
  path: string,
  method: 'PUT' | 'DELETE' | 'POST',
  body?: unknown,
): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method,
    credentials: 'include',
    headers: getAuthHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    throw new OnFarmImplantRequestError(
      response.status,
      await readProblems(response),
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const onFarmImplantSelectionOptions = (sessionId: string) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'selection', sessionId],
    async queryFn() {
      return request<OnFarmImplantSelection>(
        `/api/on-farm-implant/sessions/${sessionId}/selection`,
      );
    },
  });

export const onFarmImplantFreshMatingsOptions = (sessionId: string) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'fresh-matings', sessionId],
    async queryFn() {
      return request<OnFarmMatingRecord[]>(
        `/api/on-farm-implant/sessions/${sessionId}/fresh-matings`,
      );
    },
  });

export const onFarmImplantFreshEmbryosOptions = (
  sessionId: string,
  matingId?: string,
) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'fresh-embryos', sessionId, matingId],
    async queryFn() {
      const searchParams = new URLSearchParams();
      if (matingId) searchParams.set('matingId', matingId);
      const query = searchParams.size ? `?${searchParams.toString()}` : '';
      return request<OnFarmImplantFreshEmbryo[]>(
        `/api/on-farm-implant/sessions/${sessionId}/fresh-embryos${query}`,
      );
    },
  });

export function createOnFarmImplantFreshEmbryos(
  sessionId: string,
  input: CreateOnFarmImplantFreshEmbryosRequest,
) {
  return selectionRequest<OnFarmImplantFreshEmbryo[]>(
    `/api/on-farm-implant/sessions/${sessionId}/fresh-embryos`,
    'POST',
    input,
  );
}

export const useCreateOnFarmImplantFreshEmbryos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: CreateOnFarmImplantFreshEmbryosRequest;
    }) => createOnFarmImplantFreshEmbryos(sessionId, input),
    onSuccess: (_embryos, { sessionId }) =>
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'fresh-embryos', sessionId],
      }),
  });
};

export function editOnFarmImplantFreshEmbryos(
  sessionId: string,
  input: EditOnFarmImplantFreshEmbryosRequest,
) {
  return selectionRequest<OnFarmImplantFreshEmbryoEditResponse>(
    `/api/on-farm-implant/sessions/${sessionId}/fresh-embryos`,
    'PUT',
    input,
  );
}

export const useEditOnFarmImplantFreshEmbryos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: EditOnFarmImplantFreshEmbryosRequest;
    }) => editOnFarmImplantFreshEmbryos(sessionId, input),
    onSuccess: async (response, { sessionId }) => {
      if (!response.applied) return;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-embryos', sessionId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'queue', sessionId],
        }),
      ]);
    },
  });
};

export const onFarmImplantRecordIdsOptions = ({
  sourceType,
  siteId,
  accountId,
  search,
}: {
  sourceType: OnFarmImplantApiSourceType;
  siteId: string;
  accountId?: string;
  search?: string;
}) =>
  queryOptions({
    queryKey: [
      'on-farm-implant',
      'record-ids',
      sourceType,
      { siteId, accountId, search },
    ],
    async queryFn() {
      const searchParams = new URLSearchParams();
      searchParams.set('siteId', siteId);
      if (accountId) {
        searchParams.set('accountId', accountId);
      }
      if (search) {
        searchParams.set('search', search);
      }

      return request<OnFarmImplantRecordIds>(
        `/api/on-farm-implant/matings/${sourceType}/record-ids?${searchParams.toString()}`,
      );
    },
  });

export type OnFarmImplantSessionEmbryoStatus =
  | 'Selected'
  | 'Loaded'
  | 'Queued'
  | 'Discarded'
  | 'Frozen'
  | 'Unknown';

export type OnFarmImplantSessionEmbryo = {
  sessionEmbryoId: string;
  embryoId: string;
  embryoName: string | null;
  accountId: string | null;
  accountName: string | null;
  recordName: string | null;
  frozenStrawNumber: number | null;
  frozenStrawLabel: string | null;
  cane: string | null;
  transferStrawNumber: number | null;
  transferStrawAssigned: boolean;
  status: number;
  statusName: OnFarmImplantSessionEmbryoStatus;
  freezeMethodId: string | null;
  freezeMethodName: string | null;
  grade: number | null;
  stage: number | null;
  freezeDate: string | null;
};

export type OnFarmImplantSessionEmbryos = {
  sourceType: string;
  sourceSelected: boolean;
  items: OnFarmImplantSessionEmbryo[];
  totalRecords: number;
};

export const ON_FARM_IMPLANT_ACTIONABLE_STATUSES: OnFarmImplantSessionEmbryoStatus[] =
  ['Selected', 'Loaded'];

export function isOnFarmImplantEmbryoActionable(
  embryo: OnFarmImplantSessionEmbryo,
): boolean {
  return (
    ON_FARM_IMPLANT_ACTIONABLE_STATUSES.includes(embryo.statusName) &&
    !!embryo.freezeMethodId
  );
}

export const onFarmImplantSessionEmbryosOptions = ({
  sessionId,
  sourceType,
}: {
  sessionId: string;
  sourceType: OnFarmImplantApiSourceType;
}) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'session-embryos', sessionId, sourceType],
    async queryFn() {
      return request<OnFarmImplantSessionEmbryos>(
        `/api/on-farm-implant/sessions/${sessionId}/embryos/${sourceType}`,
      );
    },
  });

export function saveOnFarmImplantSelection(
  sessionId: string,
  input: SaveSelectionRequest,
) {
  return selectionRequest<OnFarmImplantSelection>(
    `/api/on-farm-implant/sessions/${sessionId}/selection`,
    'PUT',
    input,
  );
}

export function clearOnFarmImplantSelection(sessionId: string) {
  return selectionRequest<void>(
    `/api/on-farm-implant/sessions/${sessionId}/selection`,
    'DELETE',
  );
}

export function startOnFarmImplantSession(sessionId: string) {
  return selectionRequest<OnFarmImplantSession>(
    `/api/on-farm-implant/sessions/${sessionId}/start`,
    'POST',
  );
}

export type OnFarmImplantQueueItemInput = {
  sessionEmbryoId: string;
  transferStrawNumber: number | null;
};

export type AddOnFarmImplantQueueRequest = {
  items: OnFarmImplantQueueItemInput[];
};

export type OnFarmImplantQueueStatusName =
  | 'Pending'
  | 'Implanted'
  | 'Removed'
  | 'Unknown';

export type OnFarmImplantQueueItem = {
  sessionQueueId: string;
  sessionEmbryoId: string;
  embryoId: string;
  embryoName: string | null;
  sourceType: string;
  queueOrder: number;
  transferStrawNumber: number | null;
  frozenStrawNumber: number | null;
  status: number;
  statusName: OnFarmImplantQueueStatusName;
};

export type OnFarmImplantQueueAddResponse = {
  queued: OnFarmImplantQueueItem[];
  totalQueued: number;
};

export type OnFarmImplantQueueRecord = {
  sessionQueueId: string;
  sessionEmbryoId: string;
  embryoId: string;
  embryoName: string | null;
  sourceType: OnFarmImplantApiSourceType;
  queueOrder: number;
  accountId: string | null;
  accountName: string | null;
  recordName: string | null;
  transferStrawNumber: number | null;
  frozenStrawNumber: number | null;
  cane: string | null;
  freezeMethodId: string | null;
  freezeMethodName: string | null;
  grade: number | null;
  stage: number | null;
  embryoStatus: number;
  embryoStatusName: OnFarmImplantSessionEmbryoStatus;
};

export type OnFarmImplantQueue = {
  items: OnFarmImplantQueueRecord[];
  totalRecords: number;
};

export const onFarmImplantQueueOptions = ({
  sessionId,
}: {
  sessionId: string;
}) =>
  queryOptions({
    queryKey: ['on-farm-implant', 'queue', sessionId],
    async queryFn() {
      return request<OnFarmImplantQueue>(
        `/api/on-farm-implant/sessions/${sessionId}/queue`,
      );
    },
  });

export function addOnFarmImplantQueue(
  sessionId: string,
  sourceType: OnFarmImplantApiSourceType,
  input: AddOnFarmImplantQueueRequest,
) {
  return selectionRequest<OnFarmImplantQueueAddResponse>(
    `/api/on-farm-implant/sessions/${sessionId}/queue/${sourceType}`,
    'POST',
    input,
  );
}

export const useSaveOnFarmImplantSelection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: SaveSelectionRequest;
    }) => saveOnFarmImplantSelection(sessionId, input),
    onSuccess: (_selection, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'selection', sessionId],
      });
    },
  });
};

export const useClearOnFarmImplantSelection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId }: { sessionId: string }) =>
      clearOnFarmImplantSelection(sessionId),
    onSuccess: (_result, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'selection', sessionId],
      });
    },
  });
};

export const useStartOnFarmImplantSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId }: { sessionId: string }) =>
      startOnFarmImplantSession(sessionId),
    onSuccess: (_session, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'selection', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'sessions'],
      });
    },
  });
};

export const useAddOnFarmImplantQueue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      sourceType,
      input,
    }: {
      sessionId: string;
      sourceType: OnFarmImplantApiSourceType;
      input: AddOnFarmImplantQueueRequest;
    }) => addOnFarmImplantQueue(sessionId, sourceType, input),
    onSuccess: (_result, { sessionId, sourceType }) => {
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session-embryos', sessionId, sourceType],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'queue', sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['on-farm-implant', 'session', sessionId],
      });
      if (sourceType === 'fresh') {
        queryClient.invalidateQueries({
          queryKey: ['on-farm-implant', 'fresh-embryos', sessionId],
        });
      }
    },
  });
};
